'use client';

import { useCallback, useState } from 'react';

import { cn } from '../../lib/utils';

interface ImageCanvasProps {
  src: string;
  alt: string;
  /** Zoom percentage (100 = natural). Pan is enabled above 100. */
  zoom: number;
  className?: string;
  onError?: () => void;
}

/**
 * The shared zoom + pan + render primitive. Every image surface in the app
 * (lightbox, gallery, document viewer, media lightbox, admin identity viewer)
 * renders through this so the transform math lives in exactly one place.
 *
 * Ported from the original `@kit/documents` `ImageViewer` body.
 */
export function ImageCanvas({
  src,
  alt,
  zoom,
  className,
  onError,
}: ImageCanvasProps) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [prevSrc, setPrevSrc] = useState(src);

  const canPan = zoom > 100;

  // Reset pan when the image changes (adjust-state-during-render, not an effect).
  if (src !== prevSrc) {
    setPrevSrc(src);
    setPosition({ x: 0, y: 0 });
  }

  // Below the pan threshold the image is always centered, regardless of any
  // stored pan offset — so no effect is needed to zero it out.
  const effective = canPan ? position : { x: 0, y: 0 };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canPan) return;
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [canPan, position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !canPan) return;
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    },
    [isDragging, dragStart, canPan],
  );

  const stopDragging = useCallback(() => setIsDragging(false), []);

  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-1 items-center justify-center overflow-hidden',
        canPan && !isDragging && 'cursor-grab',
        isDragging && 'cursor-grabbing',
        className,
      )}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDragging}
      onMouseLeave={stopDragging}
    >
      {/* Storage / blob URLs cannot be optimized by Next.js Image. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full object-contain transition-transform duration-150 select-none"
        style={{
          transform: `scale(${zoom / 100}) translate(${effective.x / (zoom / 100)}px, ${effective.y / (zoom / 100)}px)`,
        }}
        onError={onError}
        draggable={false}
      />
    </div>
  );
}
